import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description'; // Excel icon
import VisibilityIcon from '@mui/icons-material/Visibility';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO, isValid } from 'date-fns';

// --- Interfaces ---
interface AuditLog {
  id: string;
  timestamp: Date;
  user: string;
  module: string;
  action: 'Add' | 'Edit' | 'Delete' | 'View' | 'Export' | 'Login' | 'Logout';
  ipAddress: string;
  details: string; // Summarized
  beforeValues?: Record<string, any>; // Optional: For Edit/Delete
  afterValues?: Record<string, any>; // Optional: For Edit/Add
}

// --- Mock Data --- 
const mockAuditLogs: AuditLog[] = [
  {
    id: 'AUD001', timestamp: new Date(2023, 10, 25, 10, 30, 15), user: 'admin.user',
    module: 'User Management', action: 'Add', ipAddress: '192.168.1.10',
    details: 'Added new user: manager.bob', afterValues: { role: 'Business Manager', status: 'Active' }
  },
  {
    id: 'AUD002', timestamp: new Date(2023, 10, 25, 11, 0, 5), user: 'manager.bob',
    module: 'Bank Management', action: 'Edit', ipAddress: '192.168.1.20',
    details: 'Updated Bank: City Bank (ID: BNK001)', beforeValues: { swiftCode: 'CITI123' }, afterValues: { swiftCode: 'CITI456' }
  },
  {
    id: 'AUD003', timestamp: new Date(2023, 10, 25, 11, 15, 40), user: 'emp.alice',
    module: 'Loan Transaction', action: 'View', ipAddress: '192.168.1.30',
    details: 'Viewed loan transaction: LN00123'
  },
  {
    id: 'AUD004', timestamp: new Date(2023, 10, 24, 16, 0, 0), user: 'admin.user',
    module: 'Role Management', action: 'Delete', ipAddress: '192.168.1.10',
    details: 'Deleted role: Temp Role (ROLE005)', beforeValues: { roleName: 'Temp Role', status: 'Inactive' }
  },
  {
    id: 'AUD005', timestamp: new Date(2023, 10, 24, 9, 5, 10), user: 'manager.bob',
    module: 'Debt Repayment Report', action: 'Export', ipAddress: '192.168.1.20',
    details: 'Exported report to Excel'
  },
];

const modules = ['User Management', 'Role Management', 'Bank Management', 'Loan Transaction', 'Debt Repayment Report', 'Login/Logout'];
const actions: AuditLog['action'][] = ['Add', 'Edit', 'Delete', 'View', 'Export', 'Login', 'Logout'];
const users = ['admin.user', 'manager.bob', 'emp.alice']; // Example users

// --- Component ---
const AuditTrailViewer: React.FC = () => {
  const [logs] = useState<AuditLog[]>(mockAuditLogs);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [filterUser, setFilterUser] = useState('');
  const [filterModule, setFilterModule] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  // Memoized filtering logic
  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      const logDate = log.timestamp;
      const isAfterStartDate = !startDate || logDate >= startDate;
      const isBeforeEndDate = !endDate || logDate <= endDate;
      const matchesUser = !filterUser || log.user === filterUser;
      const matchesModule = !filterModule || log.module === filterModule;
      const matchesAction = !filterAction || log.action === filterAction;

      return isAfterStartDate && isBeforeEndDate && matchesUser && matchesModule && matchesAction;
    });
  }, [logs, startDate, endDate, filterUser, filterModule, filterAction]);

  const handleViewDetails = (log: AuditLog) => {
    setSelectedLog(log);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedLog(null);
  };

  const handleExport = () => {
    console.log("Exporting filtered logs:", filteredLogs);
    // Add actual export logic here (e.g., using SheetJS)
    alert("Export functionality not implemented yet.");
  };

  // Format Before/After values for display
  const formatValues = (values: Record<string, any> | undefined): string => {
    if (!values) return 'N/A';
    return Object.entries(values)
      .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
      .join('\n'); // Newline separated key-value pairs
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 3 }}>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/dashboard">
          Home
        </MuiLink>
        <Typography color="text.primary">Administration</Typography>
        <Typography color="text.primary">Audit Trail Viewer</Typography>
      </Breadcrumbs>

      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Audit Trail Viewer
      </Typography>

      {/* Filters */} 
      <Paper sx={{ p: 2, mb: 3, border: '1px solid #E5E7EB', borderRadius: 2, bgcolor: '#FFFFFF' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={setStartDate}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={setEndDate}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>User</InputLabel>
              <Select value={filterUser} label="User" onChange={(e) => setFilterUser(e.target.value)}>
                <MenuItem value="">All Users</MenuItem>
                {users.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Module</InputLabel>
              <Select value={filterModule} label="Module" onChange={(e) => setFilterModule(e.target.value)}>
                <MenuItem value="">All Modules</MenuItem>
                {modules.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Action</InputLabel>
              <Select value={filterAction} label="Action" onChange={(e) => setFilterAction(e.target.value)}>
                <MenuItem value="">All Actions</MenuItem>
                {actions.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
           <Grid item xs={12} sx={{ textAlign: 'right' }}>
             <Button 
                variant="outlined" 
                startIcon={<DescriptionIcon />} 
                onClick={handleExport}
                sx={{ textTransform: 'none' }}
             >
               Export to Excel
             </Button>
           </Grid>
        </Grid>
      </Paper>

      {/* Audit Log Table */}
      <TableContainer component={Paper} sx={{ border: '1px solid #E5E7EB', borderRadius: 2 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow sx={{ '& th': { bgcolor: '#F9FAFB', fontWeight: 'bold' } }}>
              <TableCell>Activity ID</TableCell>
              <TableCell>Date & Time</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Module</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Details</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredLogs.map((log) => (
              <TableRow key={log.id} hover>
                <TableCell>{log.id}</TableCell>
                <TableCell>{format(log.timestamp, 'Pp')}</TableCell>
                <TableCell>{log.user}</TableCell>
                <TableCell>{log.module}</TableCell>
                <TableCell>
                    <Chip label={log.action} size="small" variant="outlined" />
                </TableCell>
                <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {log.details}
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => handleViewDetails(log)} title="View Details">
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {filteredLogs.length === 0 && (
                <TableRow>
                    <TableCell colSpan={7} align="center">No audit logs found matching your criteria.</TableCell>
                </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Details Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Audit Log Details</DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}><TextField label="Activity ID" value={selectedLog.id} fullWidth disabled InputLabelProps={{ shrink: true }} /></Grid>
              <Grid item xs={12} sm={6}><TextField label="Date & Time" value={format(selectedLog.timestamp, 'PPpp')} fullWidth disabled InputLabelProps={{ shrink: true }} /></Grid>
              <Grid item xs={12} sm={6}><TextField label="User" value={selectedLog.user} fullWidth disabled InputLabelProps={{ shrink: true }} /></Grid>
              <Grid item xs={12} sm={6}><TextField label="Module" value={selectedLog.module} fullWidth disabled InputLabelProps={{ shrink: true }} /></Grid>
              <Grid item xs={12} sm={6}><TextField label="Action" value={selectedLog.action} fullWidth disabled InputLabelProps={{ shrink: true }} /></Grid>
              <Grid item xs={12} sm={6}><TextField label="IP Address" value={selectedLog.ipAddress} fullWidth disabled InputLabelProps={{ shrink: true }} /></Grid>
              <Grid item xs={12}>
                <TextField 
                    label="Full Details" 
                    value={selectedLog.details} 
                    fullWidth 
                    multiline 
                    rows={2}
                    disabled 
                    InputLabelProps={{ shrink: true }}
                 />
              </Grid>
              {(selectedLog.beforeValues || selectedLog.afterValues) && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField 
                        label="Before Values" 
                        value={formatValues(selectedLog.beforeValues)} 
                        fullWidth 
                        multiline 
                        rows={4}
                        disabled 
                        InputLabelProps={{ shrink: true }}
                        sx={{ '& .MuiInputBase-inputMultiline': { whiteSpace: 'pre-wrap' } }}
                     />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField 
                        label="After Values" 
                        value={formatValues(selectedLog.afterValues)} 
                        fullWidth 
                        multiline 
                        rows={4}
                        disabled 
                        InputLabelProps={{ shrink: true }}
                        sx={{ '& .MuiInputBase-inputMultiline': { whiteSpace: 'pre-wrap' } }}
                     />
                  </Grid>
                </>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: '16px 24px' }}>
          <Button onClick={handleCloseDialog} variant="contained" sx={{ bgcolor: '#6366F1', '&:hover': { bgcolor: '#4F46E5' } }}>Close</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default AuditTrailViewer; 